import React from "react";
import { motion } from "framer-motion";
import { Home, Trash2, Building2, ArrowUpRight } from "lucide-react";

const services = [
  {
    icon: Home,
    name: "House removals",
    pitch: "Studio to 5-bed, packed, loaded, and delivered by a vetted Luton van driver.",
    range: "£250 – £950",
    examples: ["1-bed flat moves", "Family home relocations", "Student moves", "Furniture assembly add-on"],
    accent: false,
  },
  {
    icon: Trash2,
    name: "Waste clearance",
    pitch: "Garden, garage, builders, or full house clearance — gone in an afternoon.",
    range: "£180 – £620",
    examples: ["End-of-tenancy clearances", "Garden waste & green clear", "Builders rubble removal", "Probate clearances"],
    accent: true,
  },
  {
    icon: Building2,
    name: "Office moves",
    pitch: "Weekend & out-of-hours office relocations with IT handled carefully.",
    range: "£420 – £1,800",
    examples: ["Desk & workstation moves", "Server / IT-grade handling", "Multi-site relocations", "Document destruction tie-in"],
    accent: false,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-white border-b border-[#0A0A0A]/5"
    >
      <div className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
              Three services. One platform.
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl tracking-tighter font-black text-[#0A0A0A] leading-[1]">
              Built for the work
              <br />
              that actually pays.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#475569] leading-relaxed">
              Raanah skips the low-margin courier scramble. We focus on
              high-value Luton van jobs where drivers can clear real money —
              and customers get a proper, vetted operator showing up.
            </p>
          </div>
        </div>

        {/* 3-up service grid with shared borders */}
        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#0A0A0A]/10">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
                data-testid={`service-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative border-r border-b border-[#0A0A0A]/10 p-8 md:p-10 transition-colors ${
                  s.accent
                    ? "bg-[#1E4D55] text-white hover:bg-[#163C42]"
                    : "bg-white text-[#0A0A0A] hover:bg-[#F5F5F5]"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`w-12 h-12 flex items-center justify-center ${
                      s.accent ? "bg-white/10" : "bg-[#5BB247]/15"
                    }`}
                  >
                    <Icon
                      size={22}
                      className={s.accent ? "text-[#5BB247]" : "text-[#1E4D55]"}
                    />
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-[0.22em] font-bold border px-2 py-1 ${
                      s.accent
                        ? "border-white/25 text-white/80"
                        : "border-[#0A0A0A]/15 text-[#475569]"
                    }`}
                  >
                    Typical {s.range}
                  </span>
                </div>

                <h3
                  className={`font-display mt-8 text-2xl md:text-3xl font-bold tracking-tight leading-[1.1] ${
                    s.accent ? "text-white" : "text-[#0A0A0A]"
                  }`}
                >
                  {s.name}
                </h3>
                <p
                  className={`mt-3 leading-relaxed text-sm ${
                    s.accent ? "text-white/80" : "text-[#475569]"
                  }`}
                >
                  {s.pitch}
                </p>

                <ul
                  className={`mt-6 space-y-2 text-sm ${
                    s.accent ? "text-white/85" : "text-[#0A0A0A]"
                  }`}
                >
                  {s.examples.map((ex) => (
                    <li
                      key={ex}
                      className="flex items-center gap-2"
                      data-testid={`service-example-${ex.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      <span
                        className={`inline-block w-1 h-1 ${
                          s.accent ? "bg-[#5BB247]" : "bg-[#1E4D55]"
                        }`}
                      />
                      {ex}
                    </li>
                  ))}
                </ul>

                <a
                  href="#waitlist"
                  data-testid={`service-cta-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`mt-10 inline-flex items-center gap-2 text-sm font-semibold border-b transition-colors ${
                    s.accent
                      ? "text-white border-white/40 hover:border-[#5BB247] hover:text-[#5BB247]"
                      : "text-[#1E4D55] border-[#1E4D55]/30 hover:border-[#1E4D55]"
                  }`}
                >
                  <span>Book this service</span>
                  <ArrowUpRight
                    size={14}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
